import {
  useOffboardUserMutation,
  usePendingInvitesQuery,
  useResendUserInviteMutation,
} from '@/graphql/generated/hooks'

import { PendingInviteeIProps } from '../types/types'

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000

// Calculate if invite is expired (24h after lastInviteSentAt or createdAt)
const isInviteExpired = (
  createdAt: string,
  lastInviteSentAt: string | null
): boolean => {
  const referenceDate = lastInviteSentAt || createdAt
  const expiryTime = new Date(referenceDate).getTime() + TWENTY_FOUR_HOURS_MS
  return Date.now() > expiryTime
}

interface UsePendingInvitesOptionsI {
  skip?: boolean
}

export const usePendingInvites = ({
  skip = false,
}: UsePendingInvitesOptionsI = {}) => {
  const {
    data,
    loading: isLoading,
    error,
    refetch,
  } = usePendingInvitesQuery({
    fetchPolicy: 'network-only',
    skip,
  })

  const [resendUserInviteMutation] = useResendUserInviteMutation()

  const [offboardUserMutation, { loading: isRemoving }] =
    useOffboardUserMutation()

  // Map API response to UI type
  const pendingInvitees: PendingInviteeIProps[] =
    data?.pendingInvites.map((invite) => ({
      id: invite.id,
      email: invite.email ?? '',
      firstName: invite.firstName ?? '',
      lastName: invite.lastName ?? '',
      roles: invite.roles,
      invitedAt: invite.createdAt,
      lastInviteSentAt: invite.lastInviteSentAt ?? null,
      isExpired: isInviteExpired(
        invite.createdAt,
        invite.lastInviteSentAt ?? null
      ),
    })) ?? []

  const resendInvite = async (userId: string) => {
    // Note: isSsoUser is intentionally omitted - the backend should use
    // the stored value from the original invite to determine the email type
    const result = await resendUserInviteMutation({
      variables: {
        input: {
          id: userId,
        },
      },
    })
    await refetch()
    return result
  }

  const removeUser = async (userId: string) => {
    const result = await offboardUserMutation({
      variables: {
        input: {
          id: userId,
        },
      },
    })
    await refetch()
    return result
  }

  return {
    pendingInvitees,
    isLoading,
    isRemoving,
    error,
    refetch,
    resendInvite,
    removeUser,
  }
}
